/**
 * Banner Change Modal — gallery picker for banner background.
 * Mobile: bottom sheet sliding up. Desktop: centered modal.
 * Sections: Solid colors (4), Gradients (8), Preset images (44).
 * Layout: 4 items per row, responsive.
 */

import React, { useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Check, Loader2, Upload } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@turnifly/shared/api'
import { useAuthStore } from '@turnifly/shared/stores'
import { useMediaUpload } from '@turnifly/shared/hooks'
import { deleteMediaByUrl } from '@turnifly/shared/api'
import { SOLID_COLORS, GRADIENT_PRESETS, PRESET_IMAGE_COUNT } from './habit-banner'

const PRESET_IMAGE_IDS = Array.from({ length: PRESET_IMAGE_COUNT }, (_, i) => i + 1)

interface BannerChangeModalProps {
  onClose: () => void
}

type SelectionType = 'color' | 'gradient' | 'preset'

interface Selection {
  type: SelectionType
  key: string
}

function getCurrentSelection(
  bannerUrl: string | null,
  bannerTemplate: string | null,
): Selection {
  const tpl = bannerTemplate ?? 'preset_4'
  if (bannerUrl) return { type: 'preset', key: 'preset_4' }
  if (tpl.startsWith('color_')) return { type: 'color', key: tpl }
  if (tpl.startsWith('gradient_')) return { type: 'gradient', key: tpl }
  if (tpl.startsWith('preset_')) return { type: 'preset', key: tpl }
  return { type: 'preset', key: 'preset_4' }
}

export const BannerChangeModal: React.FC<BannerChangeModalProps> = ({ onClose }) => {
  const { t } = useTranslation()
  const { user, setUser } = useAuthStore()
  const [selected, setSelected] = useState<Selection>(() =>
    getCurrentSelection(user?.banner_url ?? null, user?.banner_template ?? null),
  )
  const [isSaving, setIsSaving] = useState(false)
  const { upload: uploadBanner, isUploading } = useMediaUpload()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadPreview, setUploadPreview] = useState<string | null>(null)

  const handleSelect = useCallback((type: SelectionType, key: string): void => {
    setSelected({ type, key })
    setUploadPreview(null)
  }, [])

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    if (!file || !user) return

    setUploadPreview(URL.createObjectURL(file))
    setSelected({ type: 'preset', key: '__custom__' })

    try {
      const result = await uploadBanner(file, 'banner')
      // Save custom banner URL to DB
      await supabase
        .from('profiles')
        .update({ banner_url: result.url, banner_template: 'custom', banner_position_y: 50 })
        .eq('id', user.id)
        .throwOnError()

      setUser({ ...user, banner_url: result.url, banner_template: 'custom', banner_position_y: 50 })
      onClose()
    } catch {
      setUploadPreview(null)
      setSelected(getCurrentSelection(user.banner_url ?? null, user.banner_template ?? null))
    }
  }, [user, uploadBanner, setUser, onClose])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!user || selected.key === '__custom__') return
    setIsSaving(true)

    try {
      // Delete old custom banner from R2 if switching to preset/color/gradient
      const oldBannerUrl = user.banner_url
      if (oldBannerUrl) {
        void deleteMediaByUrl(oldBannerUrl)
      }

      const updates: Record<string, unknown> = {
        banner_template: selected.key,
        banner_url: null,
        banner_position_y: 50,
      }

      await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .throwOnError()

      setUser({
        ...user,
        banner_template: selected.key,
        banner_url: null,
        banner_position_y: 50,
      })

      onClose()
    } catch {
      // Silently fail
    } finally {
      setIsSaving(false)
    }
  }, [user, selected, setUser, onClose])

  const isItemSelected = useCallback(
    (key: string): boolean => selected.key === key,
    [selected.key],
  )

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end lg:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="w-full max-w-lg max-h-[80vh] bg-surface border border-border rounded-t-[20px] lg:rounded-2xl overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header: X | Gallery | Save */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full text-text-hint hover:text-text hover:bg-white/[0.06] transition-colors"
            >
              <X size={18} />
            </button>
            <span className="text-[15px] font-display font-bold text-text">Gallery</span>
            <button
              onClick={handleSave}
              disabled={isSaving || isUploading || selected.key === '__custom__'}
              className="flex items-center gap-1.5 px-3.5 h-9 rounded-full bg-primary text-white text-[13px] font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {t('common.save')}
            </button>
          </div>

          {/* Content — scrollable */}
          <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-5">
            {/* Solid Colors */}
            <GallerySection title={t('home.bannerColors')}>
              <div className="grid grid-cols-4 gap-2">
                {SOLID_COLORS.map((c) => (
                  <SwatchButton
                    key={c.key}
                    isActive={isItemSelected(c.key)}
                    onClick={() => handleSelect('color', c.key)}
                  >
                    <div className={`w-full h-full ${c.bg}`} />
                  </SwatchButton>
                ))}
              </div>
            </GallerySection>

            {/* Gradients */}
            <GallerySection title={t('home.bannerGradients')}>
              <div className="grid grid-cols-4 gap-2">
                {GRADIENT_PRESETS.map((g) => (
                  <SwatchButton
                    key={g.key}
                    isActive={isItemSelected(g.key)}
                    onClick={() => handleSelect('gradient', g.key)}
                  >
                    <div className={`w-full h-full ${g.bg}`} />
                  </SwatchButton>
                ))}
              </div>
            </GallerySection>

            {/* Custom Upload */}
            <GallerySection title={t('home.bannerUpload')}>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className={`w-full aspect-[3/1] rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-all tap-highlight-transparent ${
                  isUploading
                    ? 'border-primary/30 bg-primary/5'
                    : 'border-white/[0.12] hover:border-primary/40 hover:bg-white/[0.02]'
                }`}
              >
                {isUploading ? (
                  <>
                    <Loader2 size={20} className="text-primary animate-spin" />
                    <span className="text-xs text-primary font-medium">{t('common.uploading')}</span>
                  </>
                ) : uploadPreview ? (
                  <img src={uploadPreview} alt="" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <>
                    <Upload size={20} className="text-text-hint/50" />
                    <span className="text-xs text-text-hint">{t('home.bannerUploadHint')}</span>
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => void handleFileChange(e)}
              />
            </GallerySection>

            {/* Preset Images */}
            <GallerySection title={t('home.bannerImages')}>
              <div className="grid grid-cols-4 gap-2">
                {PRESET_IMAGE_IDS.map((id) => {
                  const key = `preset_${id}`
                  return (
                    <SwatchButton
                      key={key}
                      isActive={isItemSelected(key)}
                      onClick={() => handleSelect('preset', key)}
                    >
                      <img
                        src={`/banners/${id}.jpg`}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                        draggable={false}
                      />
                    </SwatchButton>
                  )
                })}
              </div>
            </GallerySection>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

/* ── Section wrapper ── */

const GallerySection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h4 className="text-[12px] font-bold text-text-hint uppercase tracking-wider mb-2.5">
      {title}
    </h4>
    {children}
  </div>
)

/* ── Swatch button ── */

interface SwatchButtonProps {
  isActive: boolean
  onClick: () => void
  children: React.ReactNode
}

const SwatchButton: React.FC<SwatchButtonProps> = ({ isActive, onClick, children }) => (
  <button
    onClick={onClick}
    className={`relative aspect-[3/2] rounded-lg overflow-hidden ${
      isActive
        ? 'ring-2 ring-primary'
        : 'border border-border hover:border-white/20'
    }`}
  >
    {children}
    {isActive && (
      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
          <Check size={12} className="text-white" strokeWidth={3} />
        </div>
      </div>
    )}
  </button>
)
